import React from 'react';
import { useAtomValue, useAtom } from 'jotai';
import { Drawer, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { Retry } from '@components/common';
import ErrorBoundary from '@components/common/Retry/ErrorBoundary';
import { dimensionsListSelector } from '@store/common/dimensionList/selectors';
import useAppDimension from '../hooks/useAppDimension';
import ExplorePanel from './ExplorePanel';
import { TableWrapper, WidgetActions } from './styled';
import { TABLE_HEIGHT_MAPPING } from '../header/Resize';
import Search from './Search';
import { heightAtom, unitAtom, exploreViewAtom } from '../atoms';
import Table from '@components/common/Table';
import useGrid from '../hooks/useGrid';
import VisualSwitch, { VisualSwitchProps } from './VisualSwitch';
import { shouldMapEnabled } from './utils';
import DimensionMap, { type DimensionMapRef } from './DimensionMap';
import { WarningOutlined } from '@ant-design/icons';
import apptracker from '@utils/sensor/apptracker';
import { TOGGLE_TO_MAP_VIEW } from '@src/utils/sensor/track';
import { doesFilterContainsInvalidField, errorData } from '@src/hooks/useSetError';
import { getFirstAppliedFilter } from '@src/store/common/selectors';

function AppDimensionTable({ dimension, updateGrid }) {
  const [visualType, setVisualType] = React.useState<VisualSwitchProps['value']>('table');
  const unit = useAtomValue(unitAtom);
  const height = useAtomValue(heightAtom);
  const dimensionList = useSelector(dimensionsListSelector);
  const [exploreView, setExploreView] = useAtom(exploreViewAtom);
  const dimensionDetails = dimensionList.find((d) => d.groupBy === dimension);
  const {
    data,
    totals,
    columns,
    isFetching,
    isError,
    isEmpty,
    refetch,
    sortBy,
    onColumnSort,
    onSearch,
    searchText,
    pctShareMaxValues,
    metrics,
  } = useAppDimension({ dimension, unit });
  const mapRef = React.useRef<DimensionMapRef>(null);
  useGrid({ data, isError, isFetching, updateGrid, fixedHeight: visualType === 'map' });

  const handleVisualChange = (data: VisualSwitchProps['value']) => {
    setVisualType(data);
    if (data === 'map') {
      apptracker(TOGGLE_TO_MAP_VIEW, {
        dimension,
        unit,
        metrics: metrics.join(','),
      });
    }
  };

  React.useEffect(() => {
    mapRef.current?.resetMapCenter();
  }, [searchText]);

  const queryErrorComp = (
    <Tooltip
      placement="top"
      title={'The selected dimension does not exist. Please select another dimension.'}
    >
      <WarningOutlined style={{ color: '#ff2f36', marginRight: 10 }} /> Query Error
    </Tooltip>
  );
  const appliedFilter = useSelector(getFirstAppliedFilter);
  const filterError = doesFilterContainsInvalidField(appliedFilter);

  return (
    <>
      <WidgetActions>
        {shouldMapEnabled(dimension) && (
          <VisualSwitch value={visualType} onChange={handleVisualChange} />
        )}
        <Search onSearch={onSearch} text={searchText} />
      </WidgetActions>
      <TableWrapper style={{ height: TABLE_HEIGHT_MAPPING[height] }}>
        {errorData[dimension] || filterError ? (
          queryErrorComp
        ) : (
          <Retry
            fetchData={refetch}
            isLoading={isFetching}
            showError={isError}
            // LoadingLayer={TableSkeleton}
            isEmpty={isEmpty}
            progress={true}
          >
            {visualType === 'table' ? (
              <Table
                fixed={true}
                data={data}
                frozenData={[totals]}
                columns={columns}
                headerHeight={35}
                rowHeight={35}
                rowKey={dimension}
                sortBy={sortBy}
                headerProps={{ pctShareMaxValues }}
                onColumnSort={onColumnSort}
              />
            ) : (
              <DimensionMap
                ref={mapRef}
                defaultMetric={sortBy.key}
                dimension={dimension}
                unit={unit}
                data={data}
              />
            )}
          </Retry>
        )}
      </TableWrapper>
      {exploreView.show && exploreView.dimension === dimension && (
        <Drawer
          open={exploreView.show}
          placement="bottom"
          className="explore-drawer"
          onClose={() => setExploreView({ show: false, dimension: null })}
          destroyOnClose={true}
          title={dimensionDetails.name}
          height="90%"
        >
          <ErrorBoundary>
            <ExplorePanel
              dimension={dimension}
              data={data}
              columns={columns}
              dimensionDetails={dimensionDetails}
            />
          </ErrorBoundary>
        </Drawer>
      )}
    </>
  );
}

export default React.memo(AppDimensionTable);
